import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Plus,
  Calendar,
  Trash2,
  ChevronRight,
  LogOut,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOutfitStore } from '@/stores/outfitStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LanguageSelector } from '@/components/outfit/LanguageSelector';
import { SponsorBadge } from '@/components/SponsorBadge';

export const EventsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { events, eventsPagination, fetchEvents, fetchMoreEvents, createEvent, deleteEvent, isLoading } = useOutfitStore();

  const [newEventName, setNewEventName] = useState('');
  const [newEventDays, setNewEventDays] = useState(12);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) return;
    const event = await createEvent(newEventName, newEventDays);
    if (event) {
      setNewEventName('');
      setNewEventDays(12);
      setCreateDialogOpen(false);
      navigate(`/planner/${event.id}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    setIsDeleting(true);
    await deleteEvent(eventToDelete);
    setIsDeleting(false);
    setEventToDelete(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-dvh bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="font-serif italic font-bold text-lg text-foreground tracking-wide">
              {t('app.title')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <SponsorBadge variant="inline" />
            <LanguageSelector />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full w-9 h-9"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="mb-8 animate-spring-in">
          <h1 className="font-serif italic text-3xl sm:text-4xl font-bold text-foreground mb-1 tracking-tight">
            {t('navigation.myEvents')}
          </h1>
          <p className="text-muted-foreground font-medium">
            {user?.name ? `${t('common.hello')}, ${user.name}! ` : ''}{t('app.subtitle')}
          </p>
        </div>

        {/* Create button */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="w-full sm:w-auto mb-8 h-13 px-7 rounded-2xl bg-gradient-to-r from-[#E91E8C] to-primary hover:opacity-90 text-white font-bold shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/25 active:scale-95 active:translate-y-0 text-base"
              style={{ animationDelay: '80ms' }}
            >
              <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
              {t('event.create')}
            </Button>
          </DialogTrigger>

          <DialogContent className="rounded-3xl border-border shadow-2xl shadow-primary/10 max-w-sm w-[92vw]">
            <DialogHeader>
              <DialogTitle className="font-serif italic text-2xl text-foreground">
                {t('event.create')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="eventName" className="font-semibold text-sm text-foreground ml-1">
                  {t('event.name')}
                </Label>
                <Input
                  id="eventName"
                  value={newEventName}
                  onChange={e => setNewEventName(e.target.value)}
                  placeholder={`${t('common.example')} Cannes Film Festival 2025`}
                  onKeyDown={e => e.key === 'Enter' && handleCreateEvent()}
                  className="h-12 rounded-xl border-border bg-background/50 focus-visible:ring-primary/30 focus-visible:border-primary"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="totalDays" className="font-semibold text-sm text-foreground ml-1">
                  {t('event.totalDays')}
                </Label>
                <Input
                  id="totalDays"
                  type="number"
                  min={1}
                  max={30}
                  value={newEventDays}
                  onChange={e => setNewEventDays(parseInt(e.target.value) || 12)}
                  className="h-12 rounded-xl border-border bg-background/50 focus-visible:ring-primary/30 focus-visible:border-primary"
                />
              </div>
              <Button
                onClick={handleCreateEvent}
                disabled={!newEventName.trim() || isLoading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#E91E8C] to-primary hover:opacity-90 text-white font-bold shadow-md shadow-primary/20 active:scale-95 transition-all"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <><Plus className="w-4 h-4 mr-2" aria-hidden="true" />{t('event.create')}</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Events list */}
        {isLoading && events.length === 0 ? (
          <div className="flex justify-center py-20" aria-busy="true" aria-label="Caricamento">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 bg-white/60 backdrop-blur-sm rounded-3xl border border-dashed border-border animate-spring-in">
            <FolderOpen className="w-14 h-14 text-accent mx-auto mb-4" aria-hidden="true" />
            <h3 className="font-serif italic text-xl font-semibold text-foreground mb-1">
              {t('event.noEvents')}
            </h3>
            <p className="text-muted-foreground text-sm">{t('event.createFirst')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {events.map((event, index) => (
                <div
                  key={event.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/planner/${event.id}`)}
                  onKeyDown={e => e.key === 'Enter' && navigate(`/planner/${event.id}`)}
                  className="animate-spring-in bg-white rounded-2xl p-5 border border-border shadow-[0_2px_12px_rgba(194,24,91,0.06)] hover:shadow-[0_16px_40px_rgba(194,24,91,0.12)] transition-all duration-300 cursor-pointer group hover:-translate-y-1 hover:border-accent focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none"
                  style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
                  aria-label={`Apri evento ${event.name}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 flex-shrink-0 bg-secondary rounded-2xl flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                        <Calendar
                          className="w-5 h-5 text-primary group-hover:text-white transition-colors duration-300"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-serif italic text-lg font-bold text-foreground truncate group-hover:text-primary/80 transition-colors">
                          {event.name}
                        </h3>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">
                          {event.total_days} {t('outfit.day').toLowerCase()}s
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={e => { e.stopPropagation(); setEventToDelete(event.id); }}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 w-8 h-8 rounded-full"
                        aria-label={`Elimina ${event.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ChevronRight
                        className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load more */}
            {eventsPagination && eventsPagination.page < eventsPagination.totalPages && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={async () => { setLoadingMore(true); await fetchMoreEvents(); setLoadingMore(false); }}
                  disabled={loadingMore}
                  className="rounded-xl border-border hover:border-primary/40 hover:text-primary font-semibold"
                >
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t('common.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Sponsor footer */}
      <footer className="max-w-3xl mx-auto px-4 pb-6 pt-2">
        <SponsorBadge variant="footer" />
      </footer>

      {/* Delete confirmation */}
      <AlertDialog open={!!eventToDelete} onOpenChange={() => setEventToDelete(null)}>
        <AlertDialogContent className="rounded-3xl border-border max-w-sm w-[92vw]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif italic text-xl">
              {t('common.deleteEvent')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('event.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
